import type React from "react";

import { faChartMixed } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetTherapistPatientsPain } from "api/services/therapist/hooks";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { calculatePercentageChange } from "routes/dashboard/default/utils/calculateChange";
import { ChartToolbar } from "routes/patients/PatientProfile/components/Overview/components/ChartToolbar/ChartToolbar";
import { LineChart } from "shared/atoms/Charts";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout";
import { WidgetBox } from "shared/containers/WidgetBox";

export const PatientsPain: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: patientsPain, isLoading } = useGetTherapistPatientsPain();

  const data = patientsPain?.items || [];
  const hasData = data?.length > 0;
  const sinceStart = calculatePercentageChange(data, "avg_pain");

  const getSinceStartLabel = () => {
    if (!sinceStart || sinceStart === "0%") return "";
    if (sinceStart.startsWith("+")) return t("dashboard.patients_average_pain.increased");
    return t("dashboard.patients_average_pain.decreased");
  };

  return (
    <WidgetBox
      title={t("dashboard.patients_average_pain.title")}
      description={t("dashboard.patients_average_pain.description")}
      icon={<FontAwesomeIcon icon={faChartMixed} />}
      color="purple"
      toolbar={
        <ChartToolbar
          showDivider={false}
          sinceStart={sinceStart}
          chartType="pain"
          hasData={hasData}
          sinceStartLabel={getSinceStartLabel()}
        />
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton />}
        {!isLoading && !hasData && <NoGraphData title={t("patients.graph.no_data")} />}
        {!isLoading && hasData && (
          <LineChart
            data={data}
            height={240}
            lines={[
              {
                dataKey: "avg_pain",
                name: t("dashboard.patients_average_pain.line_name"),
                color: theme.colors.redesign.p100,
              },
            ]}
            tooltipProps={{
              color: theme.colors.redesign.p100,
              labelFormatter: (label: string) => t("common.week_num", { number: label }),
            }}
            xAxisProps={{
              dataKey: "week_number",
            }}
            yAxisProps={{
              dataKey: "avg_pain",
            }}
          />
        )}
      </Box>
    </WidgetBox>
  );
};
